import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { COMPANY_NAME, COMPANY_SHORT, COMPANY_TAGLINE, COMPANY_INITIALS } from "@/lib/brand";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (!r.ok) setErr(r.error || "Login failed");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left visual */}
      <div
        className="hidden lg:block relative bg-zinc-950"
        style={{
          backgroundImage:
            "linear-gradient(rgba(9,9,11,0.7), rgba(9,9,11,0.9)), url(https://images.unsplash.com/photo-1717386255773-1e3037c81788?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxmYWN0b3J5JTIwcHJvZHVjdGlvbiUyMGxpbmV8ZW58MHx8fHwxNzgxNTYwNDMyfDA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-orange-600 flex items-center justify-center text-white font-mono font-bold">
              {COMPANY_INITIALS}
            </div>
            <div>
              <div className="font-semibold tracking-tight text-sm leading-tight">{COMPANY_SHORT}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-0.5">
                {COMPANY_TAGLINE}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="border-l-2 border-orange-600 pl-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-500 mb-1">
                Control · Monitor · Dispatch
              </div>
              <h1 className="text-3xl font-bold tracking-tight max-w-md">
                Run your plant from a single console.
              </h1>
              <p className="text-sm text-zinc-300 mt-2 max-w-md leading-relaxed">
                {COMPANY_NAME} — operational console for daily production,
                inward inventory, and customer dispatch.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 max-w-md">
              {[
                ["PRODUCTION", "Shift-wise log"],
                ["INVENTORY", "Auto stock sync"],
                ["ORDERS", "Dispatch tracking"],
              ].map(([k, v]) => (
                <div key={k} className="border border-zinc-700 p-3">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-400">
                    {k}
                  </div>
                  <div className="text-xs text-zinc-200 mt-1">{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            StretchOps Console · v1.0 · {new Date().getFullYear()}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="relative flex items-center justify-center px-6 py-12 bg-zinc-50 overflow-hidden">
        {/* Diagonal watermark behind login form */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none" aria-hidden>
          <span
            className="font-mono font-bold uppercase whitespace-nowrap"
            style={{
              fontSize: "clamp(50px, 8vw, 110px)",
              color: "rgba(234, 88, 12, 0.06)",
              letterSpacing: "0.15em",
              transform: "rotate(-25deg)",
            }}
          >
            {COMPANY_SHORT}
          </span>
        </div>

        <form onSubmit={submit} className="w-full max-w-sm relative z-10" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 bg-orange-600 flex items-center justify-center text-white font-mono font-bold text-sm">
              {COMPANY_INITIALS}
            </div>
            <div className="font-semibold tracking-tight text-sm leading-tight">{COMPANY_SHORT}</div>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
            Authorized Access Only
          </div>
          <h2 className="text-2xl font-bold tracking-tight mb-1">Sign in to console</h2>
          <p className="text-sm text-zinc-600 mb-8">
            Use your assigned credentials to enter the production system.
          </p>

          <label className="text-xs font-semibold text-zinc-900 uppercase mb-1 block">Email</label>
          <div className="relative mb-4">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="email-input"
              type="email"
              name="email"
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full h-10 pl-9 pr-3 text-sm border border-zinc-300 bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 focus:border-orange-600"
              placeholder="Email address"
            />
          </div>

          <label className="text-xs font-semibold text-zinc-900 uppercase mb-1 block">Password</label>
          <div className="relative mb-2">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="password-input"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full h-10 pl-9 pr-3 text-sm border border-zinc-300 bg-white focus:outline-none focus:ring-1 focus:ring-orange-600 focus:border-orange-600"
              placeholder="••••••••"
            />
          </div>

          {err && (
            <div
              data-testid="login-error"
              className="border border-zinc-400 bg-zinc-100 text-zinc-900 text-xs px-3 py-2 my-3 font-mono uppercase tracking-wide"
            >
              {err}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full mt-6 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5 text-sm uppercase tracking-wider transition-colors active:scale-[0.99] flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? "Authenticating..." : (<>Enter Console <ArrowRight size={14} /></>)}
          </button>

          <div className="mt-10 text-center text-[10px] font-mono uppercase tracking-widest text-zinc-400">
            © {new Date().getFullYear()} {COMPANY_NAME}
          </div>
        </form>
      </div>
    </div>
  );
}
